import axios from 'axios'
import { Router } from 'next/router'
import { EventSenderPlugin } from '.'
import { isMetricEventLabel } from '../metricEventLabels'
import { Metadata, SendEventData } from '../types'

const DEBOUNCE_MS = 2000

export const SERVER_EVENT_PLUGIN_NAME = 'ServerEvent'
const API_PATH = '/api/server-event'

export type ServerEventLevel = 'info' | 'error'

export type ServerEventPluginOptions = {
  name?: string
  apiPath?: string
  level?: ServerEventLevel
  debounceMs?: number
  sendOnlyMetricEvents?: boolean
}

function hasErrorMetadata(event: SendEventData): boolean {
  return Boolean(event.metadata && 'error' in event.metadata)
}

export class ServerEventPlugin extends EventSenderPlugin {
  private readonly apiPath: string
  private readonly debounceMs: number
  private readonly sendOnlyMetricEvents: boolean
  private buffer: SendEventData[] = []
  private flushTimeout: ReturnType<typeof setTimeout> | null = null
  private userMetadata: Metadata = {}
  private pageHideBound: (() => void) | null = null

  constructor(options: ServerEventPluginOptions = {}) {
    super(options.name ?? SERVER_EVENT_PLUGIN_NAME)
    this.apiPath = options.apiPath ?? API_PATH
    this.debounceMs = options.debounceMs ?? DEBOUNCE_MS
    this.sendOnlyMetricEvents = options.sendOnlyMetricEvents ?? false
  }

  init() {
    if (globalThis.window === undefined) return
    this.pageHideBound = this.flushOnPageHide.bind(this)
    globalThis.window.addEventListener('pagehide', this.pageHideBound)
    try {
      Router.events.on('routeChangeStart', this.flushNow.bind(this))
    } catch {
      // next/router not available (e.g. non-Next or SSR)
    }
  }

  updateUserMetadata(metadata: Metadata): void {
    this.userMetadata = { ...this.userMetadata, ...metadata }
  }

  sendEvent(event: SendEventData) {
    if (!this.shouldEnqueue(event)) return
    this.buffer.push(event)
    this.scheduleFlush()
  }

  private shouldEnqueue(event: SendEventData): boolean {
    if (event.metadata && 'error' in event.metadata) return true
    if (!this.sendOnlyMetricEvents) return true
    return isMetricEventLabel(event.label)
  }

  /**
   * Flush immediately (e.g. on route change) so buffered events are sent before navigation.
   */
  flushNow() {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout)
      this.flushTimeout = null
    }
    this.flush()
  }

  private flushOnPageHide() {
    if (this.buffer.length === 0) return
    const events = [...this.buffer]
    this.buffer = []
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout)
      this.flushTimeout = null
    }
    this.sendToApi(events)
  }

  private scheduleFlush() {
    if (this.flushTimeout) return
    this.flushTimeout = setTimeout(() => {
      this.flush()
      this.flushTimeout = null
    }, this.debounceMs)
  }

  private flush() {
    if (this.buffer.length === 0) return
    const events = [...this.buffer]
    this.buffer = []
    this.sendToApi(events)
  }

  private buildPayload(events: SendEventData[]) {
    const payload = events.map((e) => ({
      label: e.label,
      metadata: this.serializeMetadata(e.metadata),
      level: (hasErrorMetadata(e) ? 'error' : 'info') as ServerEventLevel
    }))
    const body: {
      events: typeof payload
      userMetadata?: Record<string, unknown>
    } = { events: payload }
    if (Object.keys(this.userMetadata).length > 0) {
      body.userMetadata = this.serializeMetadata(this.userMetadata) ?? {}
    }
    return body
  }

  private async sendToApi(events: SendEventData[]) {
    try {
      const body = this.buildPayload(events)
      await axios.post(this.apiPath, body, {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' }
      })
    } catch {
      // Avoid re-throwing to not break the app
    }
  }

  private serializeMetadata(metadata?: SendEventData['metadata']) {
    if (!metadata) return undefined
    const out: Record<string, unknown> = { ...metadata }
    if (out.error instanceof Error) {
      out.error = {
        name: out.error.name,
        message: out.error.message,
        stack: out.error.stack
      }
    }
    return out
  }
}

export const SERVER_EVENT_PLUGIN_NAME_CONST = SERVER_EVENT_PLUGIN_NAME
